import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatMonthYearLabelPtBr } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/cashRetention.css';

const hasNumericValue = (value) => value !== '' && value !== null && !Number.isNaN(Number(value));

const RetencaoCaixaSPE = () => {
    const [dateTimePeriodo, setDateTimePeriodo] = useState(null);
    const [errorPeriodo, setErrorPeriodo] = useState(false);
    const [valor, setValor] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitRetencaoCaixaSPE (e) {
        e.preventDefault();
        setIsSubmit(true);

        let projeto = '';
        if (dateTimePeriodo) {
            setErrorPeriodo(false);
            const ano = dateTimePeriodo.getFullYear();
            const mes = dateTimePeriodo.getMonth() + 1;
            projeto = ano + '-' + mes;
        } else {
            setErrorPeriodo(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            projeto,
            valor
        });

        try {
            const response = await fetchFlask('retencaoCaixaSPE', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setValor('');
                setDateTimePeriodo(null);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const periodoLabel = formatMonthYearLabelPtBr(dateTimePeriodo, 'Pendente');
    const projetoLabel = dateTimePeriodo
        ? `${dateTimePeriodo.getFullYear()}-${dateTimePeriodo.getMonth() + 1}`
        : 'Não gerado';
    const valorLabel = hasNumericValue(valor) ? formatCurrency(Number(valor)) : 'Não informado';
    const completedFields = [
        dateTimePeriodo,
        hasNumericValue(valor)
    ].filter(Boolean).length;

    return (
        <Box className='mainBox cash-retention-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Retenção de Caixa SPE
            </PageHeader>

            <form action='' method='POST' id='formRetencaoCaixaSPE' onSubmit={submitRetencaoCaixaSPE} className='cash-retention-layout'>
                <Box className='cash-retention-workspace'>
                    <Box className='cash-retention-intro'>
                        <Box>
                            <h2>Retenção de caixa das SPEs no Projeto</h2>
                        </Box>
                        <p>
                            Selecione o período de competência e informe o valor que será retido antes de lançar.
                        </p>
                    </Box>

                    <Box className='cash-retention-panel'>
                        <Box className='cash-retention-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Competência do projeto</strong>
                            </Box>
                        </Box>

                        <Box className='cash-retention-period-strip'>
                            <AccountBalanceRoundedIcon />
                            <FormControl className='cash-retention-date-control' error={errorPeriodo}>
                                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        id='data'
                                        label='Projeto'
                                        views={['month', 'year']}
                                        openTo='month'
                                        minDate={new Date('2025-01-02')}
                                        maxDate={new Date()}
                                        value={dateTimePeriodo}
                                        onChange={(value) => setDateTimePeriodo(value)}
                                        slotProps={{
                                            openPickerButton: {
                                                color: 'primary'
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {errorPeriodo && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='cash-retention-panel'>
                        <Box className='cash-retention-section-heading'>
                            <SavingsRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Valor da retenção</strong>
                            </Box>
                        </Box>

                        <Box className='cash-retention-value-strip'>
                            <TrendingDownRoundedIcon />
                            <TextField
                                required
                                label='Valor'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valor}
                                onChange={(e) => setValor(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='cash-retention-rail'>
                        <Box>
                            <RequestQuoteRoundedIcon />
                            <span>Projeto</span>
                            <strong>{projetoLabel}</strong>
                        </Box>
                        <Box>
                            <PaymentsRoundedIcon />
                            <span>Operação</span>
                            <strong>Retenção SPE</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='cash-retention-summary'>
                    <Box className='cash-retention-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da retenção</strong>
                        </Box>
                    </Box>

                    <Box className='cash-retention-summary-list'>
                        <Box>
                            <span>Competência</span>
                            <strong className={!dateTimePeriodo ? 'is-empty' : undefined}>{periodoLabel}</strong>
                        </Box>
                        <Box>
                            <span>Projeto</span>
                            <strong className={!dateTimePeriodo ? 'is-empty' : undefined}>{projetoLabel}</strong>
                        </Box>
                        <Box>
                            <span>Valor</span>
                            <strong>{valorLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='cash-retention-attention'>
                        <SavingsRoundedIcon />
                        <p>
                            O lançamento será enviado com a competência selecionada e o valor de retenção informado.
                        </p>
                    </Box>

                    <Box className='cash-retention-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/2 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<TrendingDownRoundedIcon />}>
                        Lançar Retenção
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default RetencaoCaixaSPE;
